import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Users, Check, X, Loader2, Lock, Globe } from 'lucide-react';
import { fetchMyGroupInvites, acceptGroupInvite, declineGroupInvite } from '../../redux/userGroups/groups/userGroupies';
import { selectMyGroupInvites, selectFetchMyInvitesStatus } from '../../redux/userGroups/groups/groupReducer';

/**
 * Real, self-service accept/decline for group invites -- previously a
 * private-group invite could only ever be actioned by an admin (approve/
 * reject), leaving the invitee with no way to respond to their own invite at
 * all. GroupMember(status='pending') is the source of truth here; this
 * panel is what makes that state actionable rather than needing a dedicated
 * invite table.
 */
const MyGroupInvites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const invites = useSelector(selectMyGroupInvites);
  const fetchStatus = useSelector(selectFetchMyInvitesStatus);
  const [actingGroupId, setActingGroupId] = useState(null);

  useEffect(() => {
    if (fetchStatus === 'idle') dispatch(fetchMyGroupInvites());
  }, [dispatch, fetchStatus]);

  if (!invites || invites.length === 0) return null;

  const handleAccept = async (groupId) => {
    setActingGroupId(groupId);
    try {
      await dispatch(acceptGroupInvite(groupId)).unwrap();
      navigate(`/groups/${groupId}`);
    } catch {
      setActingGroupId(null);
    }
  };

  const handleDecline = async (groupId) => {
    setActingGroupId(groupId);
    try {
      await dispatch(declineGroupInvite(groupId)).unwrap();
    } finally {
      setActingGroupId(null);
    }
  };

  return (
    <div className="mb-6 bg-indigo-50 dark:bg-indigo-500/5 backdrop-blur-xl border border-indigo-300 dark:border-indigo-500/20 rounded-2xl p-6 shadow-2xl">
      <h2 className="text-lg font-semibold text-indigo-800 dark:text-indigo-200 flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        Pending Group Invites ({invites.length})
      </h2>
      <div className="space-y-2">
        {invites.map((invite) => {
          const isActing = actingGroupId === invite.group_id;
          return (
            <div
              key={invite.group_id}
              className="flex items-center justify-between gap-4 p-4 bg-white dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-white/5"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-gray-900 dark:text-white font-medium truncate">{invite.group_name}</p>
                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:border-white/10 flex-shrink-0">
                    {invite.group_privacy === 'private' ? <Lock className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {invite.group_privacy}
                  </span>
                </div>
                {invite.group_description && (
                  <p className="text-gray-500 dark:text-slate-400 text-sm truncate">{invite.group_description}</p>
                )}
                {invite.invited_by_username && (
                  <p className="text-gray-400 dark:text-slate-500 text-xs mt-1">Invited by @{invite.invited_by_username}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleAccept(invite.group_id)}
                  disabled={isActing}
                  data-testid={`accept-invite-${invite.group_id}`}
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white transition-colors"
                >
                  {isActing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  <span>Accept</span>
                </button>
                <button
                  onClick={() => handleDecline(invite.group_id)}
                  disabled={isActing}
                  data-testid={`decline-invite-${invite.group_id}`}
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-200 dark:bg-white/5 dark:hover:bg-white/10 dark:border-white/10 disabled:opacity-50 text-gray-600 dark:text-slate-300 transition-colors"
                >
                  <X className="h-4 w-4" />
                  <span>Decline</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyGroupInvites;
